/**
 * VerificationCodeInput Component
 *
 * Handles 6-digit OTP verification for email authentication.
 * Supports keyboard navigation, paste functionality, and resend timer.
 *
 * @module components/auth/VerificationCodeInput
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureException, captureEvent } from '../../lib/telemetry/posthog/frontend';
import { ButtonContent } from '../ui/ButtonContent';
import { AnimatedLabel } from '../ui/AnimatedLabel';

// ============================================================================
// TYPES
// ============================================================================

export interface VerificationCodeInputProps {
  /** Email address for OTP verification */
  email: string;
  /** Callback to trigger resend OTP */
  onResend: () => void;
  /** Whether a parent operation is in progress */
  loading: boolean;
  /** Whether resend button is disabled */
  resendDisabled: boolean;
  /** Remaining seconds until resend is available */
  resendTimer: number;
  /** Text for the submit button */
  submitButtonText?: string;
  /** Custom info text lines displayed above the code inputs */
  infoTextLines?: string[];
  /** Callback when verification succeeds */
  onVerificationSuccess: () => void;
  /** Callback when verification fails */
  onVerificationError: (errorMessage: string) => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/** Number of digits in the verification code */
const CODE_LENGTH = 6;

/** Initial empty code state */
const INITIAL_CODE_STATE = Array(CODE_LENGTH).fill('');

// ============================================================================
// STYLES
// ============================================================================

const CODE_INPUT_CLASSES =
  'w-12 h-14 text-center text-2xl font-bold bg-background border border-border rounded-lg text-foreground focus:border-volt focus:outline-none focus:ring-1 focus:ring-volt transition-colors';

const SUBMIT_BUTTON_CLASSES =
  'w-full py-4 button-accent font-bold text-lg rounded-lg transition-all duration-300 disabled:bg-surface-raised disabled:shadow-none disabled:cursor-not-allowed disabled:text-muted-foreground';

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Six-digit verification code input component for OTP authentication.
 *
 * Features:
 * - Auto-focus navigation between digits
 * - Paste support for full 6-digit codes
 * - Keyboard navigation (backspace returns to previous input)
 * - Configurable resend timer
 */
export const VerificationCodeInput: React.FC<VerificationCodeInputProps> = ({
  email,
  onResend,
  loading,
  resendDisabled,
  resendTimer,
  submitButtonText = 'Continue',
  infoTextLines = ['We have sent an email with', 'a 6 digit code number to'],
  onVerificationSuccess,
  onVerificationError,
}) => {
  const { supabase } = useSupabase();
  const [code, setCode] = useState<string[]>(INITIAL_CODE_STATE);
  const [verifying, setVerifying] = useState(false);

  // Refs for input elements to enable direct focus control
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const isSubmitDisabled = code.some((digit) => !digit) || verifying;

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Focus first input on mount for better UX.
   */
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // ============================================================================
  // INPUT HANDLERS
  // ============================================================================

  /**
   * Handles individual digit input changes.
   * Validates input and auto-advances to next field.
   */
  const handleCodeChange = useCallback(
    (index: number, value: string) => {
      // Only allow single digits
      if (value.length <= 1 && /^\d*$/.test(value)) {
        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        // Auto-advance to next input
        if (value && index < CODE_LENGTH - 1) {
          inputRefs.current[index + 1]?.focus();
        }
      }
    },
    [code]
  );

  /**
   * Handles keyboard navigation for backspace.
   * Returns to previous input when backspace is pressed on empty field.
   */
  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent) => {
      if (e.key === 'Backspace' && !code[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [code]
  );

  /**
   * Handles paste events for full verification codes.
   * Parses pasted text and fills all inputs if valid 6-digit code.
   */
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    if (new RegExp(`^\\d{${CODE_LENGTH}}$`).test(pastedData)) {
      const digits = pastedData.split('');
      setCode(digits);
      inputRefs.current[CODE_LENGTH - 1]?.focus();
      captureEvent('auth_verification_code_pasted');
    }
  }, []);

  // ============================================================================
  // SUBMISSION
  // ============================================================================

  /**
   * Handles verification form submission.
   * Verifies the OTP code with Supabase and calls appropriate callback.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitDisabled || loading) return;

    setVerifying(true);
    const fullCode = code.join('');
    captureEvent('auth_verification_submit_started');

    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: fullCode,
        type: 'email',
      });

      if (error) {
        throw error;
      }

      captureEvent('auth_verification_submit_success');
      onVerificationSuccess();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to verify code.';

      captureException(
        err instanceof Error ? err : new Error(errorMessage),
        'VerificationCodeInput.handleSubmit',
        { email_domain: email.split('@')[1] }
      );

      captureEvent('auth_verification_submit_failed', {
        error_message: errorMessage,
      });

      onVerificationError(errorMessage);
    } finally {
      setVerifying(false);
    }
  };

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Formats the resend timer display as MM:SS.
   */
  const formatResendTimer = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Info Box */}
      <div className="bg-surface p-4 rounded-lg text-sm text-center text-muted-foreground">
        {infoTextLines.map((line, idx) => (
          <p key={idx}>{line}</p>
        ))}
        <p className="font-bold text-foreground mt-1">{email}</p>
        <p className="mt-2 text-muted-foreground">Do not share this code with anyone!</p>
        <p className="text-xs mt-2 text-muted-foreground">
          You'll receive this email within 5 minutes. Be sure to check your spam folder, too.
        </p>
      </div>

      {/* Code Inputs */}
      <div className="flex justify-center gap-2" onPaste={handlePaste}>
        {code.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            maxLength={1}
            value={digit}
            onChange={(e) => handleCodeChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className={CODE_INPUT_CLASSES}
            autoComplete="one-time-code"
            inputMode="numeric"
            aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
          />
        ))}
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitDisabled || loading}
        aria-busy={verifying}
        className={SUBMIT_BUTTON_CLASSES}
      >
        <ButtonContent loading={verifying}>{submitButtonText}</ButtonContent>
      </button>

      {/* Resend Section */}
      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          Didn't receive an email?{' '}
          <button
            type="button"
            onClick={onResend}
            disabled={resendDisabled || loading || verifying}
            aria-busy={loading}
            className={`text-volt-soft hover:text-volt-soft ${
              resendDisabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <ButtonContent loading={loading}><AnimatedLabel text={resendDisabled
              ? `Resend available in ${formatResendTimer(resendTimer)}`
              : 'Send again'} /></ButtonContent>
          </button>
        </p>
      </div>
    </form>
  );
};

export default VerificationCodeInput;
